/**
 * 3D Holographic Perspective Tilt Physics Engine
 * Calculates dynamic perspective rotation angles, spring dampening,
 * and specular neon foil glare reflections on pointer interaction.
 */

export function calculateHoloTilt(clientX, clientY, rect, maxTilt = 12) {
  if (!rect || rect.width === 0 || rect.height === 0) {
    return {
      rotateX: 0,
      rotateY: 0,
      glareX: 50,
      glareY: 50,
      glareOpacity: 0,
      transform: 'perspective(800px) rotateX(0deg) rotateY(0deg)',
    };
  }

  // Normalized [0, 1] coordinates
  const normX = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  const normY = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));

  // Center is (0.5, 0.5) -> offset is [-0.5, 0.5]
  const offsetX = normX - 0.5;
  const offsetY = normY - 0.5;

  // Rotation: tilting cursor up (offsetY < 0) rotates around X positively
  const rotateX = +(offsetY * -maxTilt).toFixed(2);
  const rotateY = +(offsetX * maxTilt).toFixed(2);

  const glareX = +(normX * 100).toFixed(1);
  const glareY = +(normY * 100).toFixed(1);
  const distFromCenter = Math.sqrt(offsetX * offsetX + offsetY * offsetY);
  const glareOpacity = +(Math.min(0.35, distFromCenter * 0.7)).toFixed(2);

  return {
    rotateX,
    rotateY,
    glareX,
    glareY,
    glareOpacity,
    transform: `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.025, 1.025, 1.025)`,
  };
}

/**
 * Generates the specular radial gradient CSS style for holographic shimmer
 * @param {number} glareX 
 * @param {number} glareY 
 * @param {number} opacity 
 * @param {string} accentColor 
 */
export function getHoloGlareStyle(glareX, glareY, opacity = 0.25, accentColor = '#38bdf8') {
  return {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    borderRadius: 'inherit',
    background: `radial-gradient(circle at ${glareX}% ${glareY}%, ${accentColor}40 0%, rgba(255, 255, 255, ${opacity * 0.6}) 25%, transparent 65%)`,
    opacity,
    mixBlendMode: 'screen',
    transition: 'opacity 0.2s ease',
    zIndex: 5,
  };
}
